// =====================================================
// Message Bubble - Individual Message Display
// =====================================================

import React, { useState } from 'react';
import { Message } from '../types';
import ButtonGroup from './ButtonGroup';
import Dropdown from './Dropdown';
import HomeCard from './cards/HomeCard';
import CommunityCard from './cards/CommunityCard';
import FloorPlanCard from './cards/FloorPlanCard';
import CardCarousel from './CardCarousel';
import TourRequestForm from './TourRequestForm';

interface MessageBubbleProps {
  message: Message;
}

/**
 * Parse text with markdown-style formatting:
 * - [link text](url) -> clickable link with hidden URL
 * - **bold text** -> bold formatting
 * - Plain URLs -> clickable links
 * - {{OPEN_FORM}}text{{/OPEN_FORM}} -> clickable link to open form
 */
const parseTextWithLinks = (
  text: string,
  onOpenForm?: () => void
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let remainingText = text;
  let keyCounter = 0;

  // Process markdown links [text](url), bold **text**, plain URLs, and {{OPEN_FORM}} markers
  while (remainingText.length > 0) {
    // Try to match markdown link [text](url)
    const markdownLinkMatch = remainingText.match(/\[([^\]]+)\]\(([^)]+)\)/);

    // Try to match bold **text**
    const boldMatch = remainingText.match(/\*\*([^*]+)\*\*/);

    // Try to match plain URL
    const plainUrlMatch = remainingText.match(/(https?:\/\/[^\s]+)/);

    // Try to match {{OPEN_FORM}}text{{/OPEN_FORM}}
    const openFormMatch = remainingText.match(/\{\{OPEN_FORM\}\}(.+?)\{\{\/OPEN_FORM\}\}/);

    // Find which match comes first
    let firstMatch = null;
    let matchType = '';
    let matchIndex = Infinity;

    if (markdownLinkMatch && markdownLinkMatch.index !== undefined && markdownLinkMatch.index < matchIndex) {
      firstMatch = markdownLinkMatch;
      matchType = 'markdown';
      matchIndex = markdownLinkMatch.index;
    }
    if (boldMatch && boldMatch.index !== undefined && boldMatch.index < matchIndex) {
      firstMatch = boldMatch;
      matchType = 'bold';
      matchIndex = boldMatch.index;
    }
    if (plainUrlMatch && plainUrlMatch.index !== undefined && plainUrlMatch.index < matchIndex) {
      firstMatch = plainUrlMatch;
      matchType = 'url';
      matchIndex = plainUrlMatch.index;
    }
    if (openFormMatch && openFormMatch.index !== undefined && openFormMatch.index < matchIndex) {
      firstMatch = openFormMatch;
      matchType = 'openForm';
      matchIndex = openFormMatch.index;
    }

    // No more matches found
    if (!firstMatch || firstMatch.index === undefined) {
      if (remainingText.length > 0) {
        parts.push(remainingText);
      }
      break;
    }

    // Add text before the match
    if (firstMatch.index > 0) {
      parts.push(remainingText.substring(0, firstMatch.index));
    }

    // Add the matched element
    if (matchType === 'markdown') {
      const linkText = firstMatch[1];
      const url = firstMatch[2];
      parts.push(
        <a
          key={`link-${keyCounter++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {linkText}
        </a>
      );
    } else if (matchType === 'bold') {
      const boldText = firstMatch[1];
      parts.push(
        <strong key={`bold-${keyCounter++}`} className="font-bold">
          {boldText}
        </strong>
      );
    } else if (matchType === 'url') {
      const url = firstMatch[0];
      parts.push(
        <a
          key={`url-${keyCounter++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {url}
        </a>
      );
    } else if (matchType === 'openForm') {
      const linkText = firstMatch[1];
      parts.push(
        <button
          key={`form-${keyCounter++}`}
          onClick={onOpenForm}
          className="text-betenbough-olive hover:text-betenbough-olive/80 underline font-medium
                     cursor-pointer bg-transparent border-none p-0 inline"
        >
          {linkText}
        </button>
      );
    }

    // Move past the match
    remainingText = remainingText.substring(firstMatch.index + firstMatch[0].length);
  }

  return parts.length > 0 ? parts : [text];
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.sender === 'user';
  // State for managing inline appointment form visibility
  const [showForm, setShowForm] = useState(message.showAppointmentForm || false);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[95%] sm:max-w-[90%]`}>
        {/* Message Text with Avatar */}
        <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-end gap-1`}>
          {/* Bot Avatar - only for bot messages */}
          {!isUser && (
            <div className="w-5 h-5 rounded-full bg-white flex-shrink-0 flex items-center justify-center overflow-hidden shadow-sm">
              <img
                src="/ava-logo.png"
                alt="Ava"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = '<span class="text-betenbough-gunmetal font-bold text-[10px]">A</span>';
                }}
              />
            </div>
          )}
          <div className="relative">
            {/* Tooltip-style tail for bot messages */}
            {!isUser && (
              <div className="absolute -left-[3px] bottom-2
                              w-0 h-0 border-t-[5px] border-t-transparent
                              border-b-[5px] border-b-transparent
                              border-r-[5px] border-r-white" />
            )}
            <div
              className={`message-bubble ${isUser ? 'user' : 'bot'} animate-slide-up`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {parseTextWithLinks(message.text, () => setShowForm(true))}
              </p>
            </div>
          </div>
        </div>

        {/* Carousel or Single Card */}
        {message.cards && message.cards.length > 0 ? (
          <div className={`mt-2 ${!isUser ? 'ml-6 w-[calc(100%-1.5rem)]' : 'w-full'}`}>
            <CardCarousel cards={message.cards} />
          </div>
        ) : message.card ? (
          <div className={`mt-2 ${!isUser ? 'ml-6 w-[calc(100%-1.5rem)]' : 'w-full'}`}>
            {message.card.type === 'home' && (
              <HomeCard card={message.card} />
            )}
            {message.card.type === 'community' && (
              <CommunityCard card={message.card} />
            )}
            {message.card.type === 'floor_plan' && (
              <FloorPlanCard card={message.card} />
            )}
          </div>
        ) : null}

        {/* Inline Appointment Form (if triggered) */}
        {showForm && (
          <div className={`mt-2 ${!isUser ? 'ml-6 w-[calc(100%-1.5rem)]' : 'w-full'}`}>
            <TourRequestForm
              isOpen={showForm}
              onClose={() => setShowForm(false)}
              prefillData={{
                inquiryType: 'general'
              }}
            />
          </div>
        )}

        {/* Dropdown (if present) */}
        {message.dropdown && message.dropdown.length > 0 && (
          <div className={`mt-2 ${!isUser ? 'ml-6 w-[calc(100%-1.5rem)]' : 'w-full'}`}>
            <Dropdown options={message.dropdown} placeholder="Select an option..." />
          </div>
        )}

        {/* Buttons (if present) - hide if form is shown */}
        {message.buttons && message.buttons.length > 0 && !showForm && (
          <div className={`mt-2 ${!isUser ? 'ml-6 w-[calc(100%-1.5rem)]' : 'w-full'}`}>
            <ButtonGroup buttons={message.buttons} />
          </div>
        )}

        {/* Timestamp */}
        <span className={`text-xs text-betenbough-gunmetal opacity-60 mt-1 font-filson ${!isUser ? 'ml-6' : ''}`}>
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
    </div>
  );
};

export default MessageBubble;